<template>
    <modal-dialog
        ref="modalDialog"
        @close="reset"
        :isLoading="isSaving"
        confirmBtnTxt="Save"
        @confirm-dialog="save"
    >
        <template v-slot:title>
            {{ title }}
        </template>
        <template v-slot:body>
            <template>
                <div class="mb-2">
                    Set the start and end time for this block. Regular hours
                    refer to the standard clinic operating hours. After hours
                    indicates extended service time outside regular hours, in
                    which the clinic may still operate in a limited capacity. An
                    after hours block triggers the automatic % markups on prices
                    set in Configurations. Choose the appropriate time range and
                    save your changes.
                </div>

                <div
                    v-if="errorMessage"
                    class="alert alert-danger"
                    role="alert"
                >
                    {{ errorMessage }}
                </div>

                <div class="form-group form-row">
                    <label for="day" class="col-md-4 col-form-label"
                        >Day:</label
                    >
                    <div class="col-8 col-form-label">
                        {{ displayDay(day.day) }}
                    </div>
                </div>
                <div class="form-group form-row">
                    <label for="day" class="col-md-4 col-form-label"
                        >Hours Range:</label
                    >
                    <div class="col-md-8 d-flex align-items-center">
                        <timepicker
                            v-validate="'required'"
                            v-model="form.start_time"
                            name="start_time"
                            class="form-control"
                            placeholder="Start (required)"
                            @input="startTimeUpdated"
                            :class="{
                                'border-danger': errors.has('start_time')
                            }"
                        />
                        <span class="mx-2">-</span>
                        <timepicker
                            v-validate="'required'"
                            v-model="form.end_time"
                            name="end_time"
                            class="form-control"
                            placeholder="End (required)"
                            @input="endTimeUpdated"
                            :class="{
                                'border-danger': errors.has('end_time')
                            }"
                        />
                    </div>
                </div>

                <div class="form-group form-row">
                    <label for="is-after-hour" class="col-md-4 col-form-label"
                        >Hours Type:</label
                    >
                    <div class="col">
                        <select
                            v-model="form.is_after_hour"
                            name="is_after_hour"
                            id="is-after-hour"
                            class="form-control custom-select"
                        >
                            <option
                                v-for="type in typeOptions"
                                :key="type.value"
                                :value="type.value"
                            >
                                {{ type.label }}
                            </option>
                        </select>
                    </div>
                </div>

                <div
                    class="alert alert-block alert-warning padding-alert mb-0"
                    v-if="form.is_after_hour"
                >
                    Changing this to after hours may impact inventory pricing
                    and online booking fees.
                </div>
            </template>
        </template>
    </modal-dialog>
</template>

<script>
import { isEmpty } from "lodash";
import Timepicker from "@/module/commons/time-picker";
import ModalDialog from "@/components/commons/modal-dialog";
import ClinicHourService from "@/services/setting/clinic-hour";
import CLINIC_HOUR from "@/constants/clinic-hour";
import { DateTime } from "luxon";

const defaultForm = () => {
    return {
        id: null,
        start: null,
        end: null,
        start_time: null,
        end_time: null,
        is_after_hour: 0
    };
};

export default {
    components: {
        ModalDialog,
        Timepicker
    },
    props: {
        clinicDays: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            isSaving: false,
            day: 0, // sunday
            form: defaultForm(),
            errorMessage: null
        };
    },
    created() {
        // Custom validator for time range
        this.$validator.extend("time_range", {
            getMessage: field =>
                `The ${field} must be later than the start time`,
            validate: () => this.validateTimeRange()
        });
    },
    computed: {
        title() {
            return this.isEdit
                ? "Edit Booking Hours block"
                : "Add Booking Hours block";
        },
        isEdit() {
            return !isEmpty(this.form?.id);
        },
        typeOptions() {
            return [
                {
                    value: 0,
                    label: "Regular Hours"
                },
                {
                    value: 1,
                    label: "After Hours"
                }
            ];
        }
    },
    watch: {},
    methods: {
        async open(day, event = null) {
            this.$refs.modalDialog.open();

            if (event) {
                this.form.id = event.id;
                this.form.start = event.start;
                this.form.end = event.end;
                this.form.start_time = this.getTime(event.start);
                this.form.end_time = this.getTime(event.end);
                this.form.is_after_hour = event.is_after_hour;
            }

            this.day = day;
        },
        async save() {
            const isValid = await this.$validator.validateAll();

            const isTimeRangeValid = this.validateTimeRange();

            const isNoOverlap = !this.isBookingHourOverlap();

            if (!isValid || !isTimeRangeValid || !isNoOverlap) {
                if (!isTimeRangeValid) {
                    this.errorMessage =
                        "End time must be later than the start time. Please select a valid time range.";
                    return;
                }
                if (!isNoOverlap) {
                    this.errorMessage =
                        "Clinic hours overlap with an existing time block. Please choose a different time.";
                    return;
                }
                return;
            }

            this.isSaving = true;

            try {
                let res;

                if (this.isEdit) {
                    res = await ClinicHourService.updateClinicHour(
                        this.form.id,
                        {
                            start: this.form.start,
                            end: this.form.end,
                            is_after_hour: this.form.is_after_hour
                        }
                    );
                } else {
                    res = await ClinicHourService.createClinicHour({
                        clinic_day_id: this.day.id,
                        start: this.form.start,
                        end: this.form.end,
                        is_after_hour: this.form.is_after_hour
                    });
                }

                if (res?.response?.code === 200) {
                    this._message("Clinic hour successfully saved");
                    this.$emit("success");
                    this.$refs.modalDialog.close();
                    this.reset();
                } else {
                    throw new Error("Error: Unable to save clinic hour");
                }
            } catch (error) {
                console.error(error);
                this._message("Error : Unable to update clinic hour", "danger");
            } finally {
                this.isSaving = false;
            }
        },
        reset() {
            this.$validator.reset();
            Object.assign(this.form, defaultForm());
            this.errorMessage = null;
        },
        displayDay(day) {
            return CLINIC_HOUR.DAYS[String(day)];
        },
        isBookingHourOverlap() {
            const currentStartMinutes = this.form.start
                ? this.form.start
                : this.getMinutes(this.form.start_time);
            const currentEndMinutes = this.form.end
                ? this.form.end
                : this.getMinutes(this.form.end_time);

            let clinicHours = this.day.clinic_hours;

            if (this.isEdit) {
                clinicHours = clinicHours.filter(
                    hour => hour.id !== this.form.id
                );
            }

            return clinicHours.some(hour => {
                const startMinutes = hour.start;
                const endMinutes = hour.end;

                if (
                    currentStartMinutes >= startMinutes &&
                    currentStartMinutes <= endMinutes
                ) {
                    return true;
                }
                if (
                    currentEndMinutes >= startMinutes &&
                    currentEndMinutes <= endMinutes
                ) {
                    return true;
                }
                if (
                    currentStartMinutes <= startMinutes &&
                    currentEndMinutes >= endMinutes
                ) {
                    return true;
                }

                return false;
            });
        },
        getMinutes(dateObject) {
            if (!dateObject || !dateObject.c) {
                return null;
            }

            // Extract hours and minutes
            const hours = dateObject.c.hour;
            const minutes = dateObject.c.minute;

            // Convert hours and minutes to total minutes
            const totalMinutes = hours * 60 + minutes;
            return totalMinutes;
        },
        getTime(totalMinutes) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            const specificDate = DateTime.now().set({
                hour: hours,
                minute: minutes,
                second: 0
            });

            const formattedTime = specificDate.toFormat("hh:mm a");

            return formattedTime;
        },
        startTimeUpdated() {
            const startMinutes = this.getMinutes(this.form.start_time);
            this.form.start = startMinutes;

            // Validate end time when start time changes
            if (this.form.end_time) {
                this.$validator.validate("end_time");
            }
        },
        endTimeUpdated() {
            const endMinutes = this.getMinutes(this.form.end_time);
            this.form.end = endMinutes;

            // Validate end time
            this.$validator.validate("end_time");
        },
        validateTimeRange() {
            const startMinutes = this.form.start
                ? this.form.start
                : this.getMinutes(this.form.start_time);
            const endMinutes = this.form.end
                ? this.form.end
                : this.getMinutes(this.form.end_time);

            // Check if end time is strictly greater than start time
            if (endMinutes <= startMinutes) {
                this.$validator.errors.add({
                    field: "end_time",
                    msg: "End time must be later than start time",
                    rule: "time_range"
                });
                return false;
            }

            return true;
        }
    }
};
</script>

<style scoped>
.padding-alert {
    padding: 10px;
}
</style>
